import React, { FC, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
	EvBodyDefault,
	EvFlexColumn,
	EvFlexRow,
	EvHeadingLarge,
	EvIcon,
	EvLink,
	FLEX_ALIGN,
	FLEX_GAP,
	SmallBackIcon
} from '@evinced-private/ui-common';

import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { UNIQUE_QUERY_PARAM } from 'src/common/helpers/QueryParamsHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';

import { IssuesFilters } from './components/IssuesFilters';
import { useIssuesPageFilters } from './hooks/useIssuesPageFilters';
import { ISSUES_PAGE_CONSTANTS } from './IssuesPage.constants';

import './IssuesPage.scss';

export const IssuesPage: FC = () => {
	const location = useLocation();
	const history = useHistory();
	const [filters, setFilters] = useState<IFilterOptions>({});

	usePlatformConfigInitializer();

	const { filterDefinitions, filterOptions } = useIssuesPageFilters();

	const isUnique = useMemo(() => {
		const searchParams = new URLSearchParams(location.search);
		return searchParams.get(UNIQUE_QUERY_PARAM) === 'true';
	}, [location.search]);

	const title = isUnique ? ISSUES_PAGE_CONSTANTS.TITLE.UNIQUE : ISSUES_PAGE_CONSTANTS.TITLE.ALL;
	const description = isUnique
		? ISSUES_PAGE_CONSTANTS.DESCRIPTION.UNIQUE
		: ISSUES_PAGE_CONSTANTS.DESCRIPTION.ALL;

	const backLinkContent = (
		<EvFlexRow gap={FLEX_GAP.SMALL} align={FLEX_ALIGN.CENTER}>
			<EvIcon icon={SmallBackIcon} small />
			<span>{ISSUES_PAGE_CONSTANTS.BACK_BUTTON_TEXT}</span>
		</EvFlexRow>
	);

	const handleBackClick = (): void => {
		history.push(RoutesHelper.getAccessibilityDashboardPath());
	};

	const handleFilterChange = (newFilters: IFilterOptions): void => {
		setFilters(newFilters);
	};

	return (
		<div className="issues-page">
			<EvFlexColumn gap={FLEX_GAP.MEDIUM}>
				<div className="issues-page__back-link">
					<EvLink
						url={RoutesHelper.getAccessibilityDashboardPath()}
						customLink={backLinkContent}
						onClick={handleBackClick}
					/>
				</div>

				<EvFlexColumn gap={FLEX_GAP.SMALL}>
					<EvHeadingLarge id="issues-page-title">{title}</EvHeadingLarge>
					<EvBodyDefault id="issues-page-description">{description}</EvBodyDefault>
				</EvFlexColumn>

				<IssuesFilters
					filters={filters}
					onFilterChange={handleFilterChange}
					filterDefinitions={filterDefinitions}
					filterOptions={filterOptions}
				/>
			</EvFlexColumn>
		</div>
	);
};
